import { Controller } from "@hotwired/stimulus"
import { collectAllSpans, findValueMatches, matchAllFields, matchFieldsChunked } from "../lib/pdf_review/matcher"
import { pagesNeedingWordBoxes, fillAvailableTextLayers } from "../lib/pdf_review/word_box_layer"
import { HighlightOverlay } from "../lib/pdf_review/highlight_overlay"
import { ReviewStore } from "../lib/pdf_review/review_store"
import { fieldLabel } from "../lib/field_label"
import { displayFor, leafKey } from "../lib/value_format"

/**
 * PDF Review Controller
 *
 * Orchestrates the review mode: matching extracted data fields to their locations
 * on the rendered PDF, rendering highlight overlays, managing bidirectional
 * click navigation, and tracking review state.
 */
export default class extends Controller {
  static targets = [
    "viewerContainer",
    "dataPanel",
    "fieldRow",
    "toggleBtn",
    "progressChip",
    "sectionHeader",
    "guide"
  ]
  static values = {
    paperworkId: String,
    isPublic: { type: Boolean, default: false },
    reviewStateUrl: String,
    reviewableFieldsUrl: String,
    wordBoxesUrl: String,
    fields: { type: Array, default: [] },
    active: { type: Boolean, default: false },
    // A field a task's text linked to (field=line_items.0.unit_price): the
    // data panel opens on its row, selected and flashed. Review is not turned
    // on, so no keypress records a review by accident.
    focusField: { type: String, default: "" }
  }

  // Ceiling on box fetches for one document, however many scanned pages it has. Reached
  // only by a long scan whose pages a reader actually scrolls through.
  static WORD_BOX_PAGE_BUDGET = 30

  // Review carries on from one paperwork to the next: leaving a document
  // while reviewing leaves a short-lived note, and the document that opens
  // within this window starts in review. Closing the viewer and coming back
  // later is outside the window, so it does not.
  static CONTINUE_KEY = 'viewer-review-continue-until'
  static CONTINUE_MS = 4000

  connect() {
    this.overlay = null
    this.store = null
    // pageNumber -> 'loading' | 'unavailable' | page. Every outcome is recorded so a
    // page is requested at most once.
    this._wordBoxPages = new Map()
    this.matchResults = null
    this.pageSpans = null
    this._pdfReady = false
    this._matchDebounce = null
    this._statusDebounce = null
    this._focusedFieldIndex = -1
    this._matchingInProgress = false
    this._reviewableFieldsLoaded = this.fieldsValue.length > 0

    // Bind handlers so we can remove them later
    this._boundPdfReady = this.handlePdfReady.bind(this)
    this._boundPageRendered = this.handlePageRendered.bind(this)
    this._boundReviewClickCapture = this.handleReviewClickCapture.bind(this)
    this._boundKeydown = this.handleKeydown.bind(this)
    this._boundFieldUpdated = this.handleFieldUpdated.bind(this)

    // Listen for pdf-viewer ready events (bubble up from child elements)
    this.element.addEventListener('pdf-viewer:pages-ready', this._boundPdfReady)
    this.element.addEventListener('pdf-viewer:page-rendered', this._boundPageRendered)
    this.element.addEventListener('click', this._boundReviewClickCapture, true)
    this.element.addEventListener('extracted-data-editor:field-updated', this._boundFieldUpdated)

    this.scheduleStatusPrefetch()
    if (this.focusFieldValue) window.setTimeout(() => this.landOnField(this.focusFieldValue), 60)
  }

  landOnField(fieldPath) {
    // The row may sit in a closed section or an array item not yet drawn.
    const revealed = this.dataEditorController()?.revealField?.(fieldPath)
    if (revealed) this._fieldRowMap = null
    if (!this._fieldRowByPath(fieldPath)) return

    this.selectFieldRow(fieldPath)
    this.scrollToField(fieldPath)
  }

  disconnect() {
    clearTimeout(this._prefetchTimer)
    if (this._prefetchIdle && window.cancelIdleCallback) window.cancelIdleCallback(this._prefetchIdle)
    if (this.activeValue) this.noteReviewContinues()
    this.deactivateReview()
    this.element.removeEventListener('pdf-viewer:pages-ready', this._boundPdfReady)
    this.element.removeEventListener('pdf-viewer:page-rendered', this._boundPageRendered)
    this.element.removeEventListener('click', this._boundReviewClickCapture, true)
    this.element.removeEventListener('extracted-data-editor:field-updated', this._boundFieldUpdated)
    document.removeEventListener('keydown', this._boundKeydown)
  }

  // --- Toggle ---

  toggleReview() {
    this.activeValue = !this.activeValue
    if (!this.activeValue) this.clearReviewContinues()
  }

  // The viewer modal's rail closed (viewer_panel_controller#toggle): the rows
  // are hidden, so the review's keys must stop acting on them.
  endReview() {
    if (!this.activeValue) return
    this.activeValue = false
    this.clearReviewContinues()
  }

  activeValueChanged() {
    if (this.activeValue) {
      this.activateReview()
    } else {
      this.deactivateReview()
    }
    this.updateToggleButton()
  }

  async activateReview() {
    this.element.classList.add('pdf-review-active')
    this.enterDataEditorReviewMode()

    // The store the status prefetch made is reused; hydrating again picks up
    // anything another person has checked since.
    this.store ||= this.buildReviewStore()
    await this.store.hydrate()
    try {
      await this.loadReviewableFields()
      await this.ensureReviewFormRows()
    } catch (error) {
      console.error("Unable to load reviewable fields:", error)
      this.activeValue = false
      this.deactivateReview()
      return
    }
    this._hasPlayedIntroPulse = false
    this._focusedFieldIndex = -1
    this._retryCount = 0
    this._confirmingRest = false
    this._showDone = undefined
    this.seedMatchResults()

    // Bind keyboard shortcuts
    document.addEventListener('keydown', this._boundKeydown)

    // Try to run matching immediately if PDF is already rendered,
    // or poll briefly waiting for text layers to appear
    this.waitForTextLayerAndMatch()

    // Watch for new text layer content (pdf.js renders spans asynchronously)
    this.observeTextLayers()

    this.showFieldReviewUI()
    this.expandReviewSections()
    this.updateFieldStatuses()
    if (this.hasGuideTarget) this.guideTarget.classList.remove('hidden')
    this.ensureFocusedField()
    this.updateGuide()
  }

  /**
   * Every reviewable field starts as "not located". An email, a spreadsheet, or
   * a PDF that has not rendered yet has nothing to match against, and review
   * must still walk through each field. Matching against a rendered PDF
   * replaces these results when it runs.
   */
  seedMatchResults() {
    if (this.matchResults) return

    this.matchResults = new Map(
      this.fieldsValue
        .filter(field => field.path)
        .map(field => [field.path, { status: 'unmatched', matches: [], confidence: 0 }])
    )
  }

  async loadReviewableFields() {
    if (this._reviewableFieldsLoaded || !this.hasReviewableFieldsUrlValue) return

    const response = await fetch(this.reviewableFieldsUrlValue, {
      headers: { "Accept": "application/json" },
    })
    if (!response.ok) {
      throw new Error(`Unable to load reviewable fields (${response.status})`)
    }

    this.fieldsValue = await response.json()
    this._reviewableFieldsLoaded = true
  }

  async ensureReviewFormRows() {
    if (this.fieldsValue.length === 0) return

    // Large arrays render lazily; review matching needs every row in the DOM.
    this.dataEditorController()?.materializeAll?.()
    if (this.fieldRowTargets.length > 0) return

    this.enterDataEditorReviewMode()
    await new Promise(resolve => requestAnimationFrame(resolve))

    if (this.fieldRowTargets.length === 0) {
      throw new Error("Review mode requires extracted-data form rows, but none were found.")
    }
  }

  deactivateReview() {
    this.element.classList.remove('pdf-review-active')
    this.exitDataEditorReviewMode()
    this.overlay?.clear()
    this.overlay = null
    this._viewerEl = null
    this.matchResults = null
    this._confirmingRest = false
    clearTimeout(this._confirmRestTimer)
    if (this.hasGuideTarget) {
      this.guideTarget.classList.add('hidden')
      this.guideTarget.innerHTML = ''
    }
    document.removeEventListener('keydown', this._boundKeydown)
    this._textLayerObserver?.disconnect()
    this._textLayerObserver = null
    this.hideFieldReviewUI()
    if (this.hasProgressChipTarget) {
      this.progressChipTarget.classList.add('hidden')
    }
  }

  // --- PDF events ---

  handlePdfReady() {
    this._pdfReady = true
    if (this.activeValue) {
      // Wait for text layers to render after pages init
      setTimeout(() => this.runMatching(), 800)
    }
  }

  /**
   * Fetches boxes for pages that rendered without a text layer — one request per page.
   *
   * Bounded on purpose. Only scanned pages ask for anything, only pages currently in the
   * viewer are asked for, and no more than WORD_BOX_PAGE_BUDGET pages are fetched for a
   * document however long it is. A 500-page scan therefore costs what the pages on
   * screen cost, and never stalls the review panel waiting for the rest.
   *
   * Every outcome is recorded, including failure, so a page is never requested twice.
   */
  loadWordBoxesFor(pageNumbers) {
    if (!this.hasWordBoxesUrlValue) return

    const budget = this.constructor.WORD_BOX_PAGE_BUDGET - this._wordBoxPages.size
    if (budget <= 0) return

    pageNumbers.slice(0, budget).forEach(pageNumber => {
      this._wordBoxPages.set(pageNumber, 'loading')

      const url = `${this.wordBoxesUrlValue}?page=${pageNumber}`
      fetch(url, { headers: { Accept: 'application/json' } })
        .then(response => (response.ok ? response.json() : null))
        .catch(() => null)
        .then(payload => {
          this._wordBoxPages.set(pageNumber, payload?.page || 'unavailable')
          // Debounced with the same timer the render events use, so a burst of pages
          // landing together triggers one match rather than one each.
          clearTimeout(this._matchDebounce)
          this._matchDebounce = setTimeout(() => this.runMatching(), 150)
        })
    })
  }

  handlePageRendered() {
    if (this.activeValue) {
      this._pdfReady = true
      // Debounce: pages render in sequence, wait for them to settle
      clearTimeout(this._matchDebounce)
      this._matchDebounce = setTimeout(() => this.runMatching(), 600)
    }
  }

  /**
   * Poll for text layer spans. The PDF may already be rendered before
   * review mode is toggled on, so we can't rely solely on events.
   */
  waitForTextLayerAndMatch(attempts = 0) {
    const viewer = this.findPdfViewer()
    if (!viewer) {
      if (attempts < 20) {
        setTimeout(() => this.waitForTextLayerAndMatch(attempts + 1), 300)
      }
      return
    }

    const textSpans = viewer.querySelectorAll('.textLayer span')
    if (textSpans.length > 0) {
      this._pdfReady = true
      this.runMatching()
    } else if (attempts < 20) {
      setTimeout(() => this.waitForTextLayerAndMatch(attempts + 1), 300)
    }
  }

  /**
   * MutationObserver on text layers — re-runs matching when pdf.js adds
   * new spans (it renders them asynchronously even after the page appears).
   */
  observeTextLayers(attempts = 0) {
    const viewer = this.findPdfViewer()
    if (!viewer) {
      if (attempts < 20) {
        setTimeout(() => this.observeTextLayers(attempts + 1), 300)
      }
      return
    }

    this._textLayerObserver?.disconnect()
    this._textLayerObserver = new MutationObserver((mutations) => {
      if (!this.activeValue) return
      if (this._matchingInProgress) return // Don't re-trigger while chunked match runs
      if (!mutations.some(mutation => this.isTextLayerMutation(mutation))) return

      clearTimeout(this._observerDebounce)
      this._observerDebounce = setTimeout(() => {
        this._retryCount = 0
        this.runMatching()
      }, 600)
    })

    // Observe all text layers for added child/text content
    viewer.querySelectorAll('.textLayer').forEach(layer => {
      this._textLayerObserver.observe(layer, { childList: true, characterData: true, subtree: true })
    })

    // Also observe the whole viewer because PDF.js may add textLayer content
    // inside existing page nodes after review mode has already been enabled.
    this._textLayerObserver.observe(viewer, { childList: true, characterData: true, subtree: true })
  }

  isTextLayerMutation(mutation) {
    const target = mutation.target.nodeType === Node.ELEMENT_NODE
      ? mutation.target
      : mutation.target.parentElement

    if (target?.closest?.('.pdf-review-overlay')) return false
    if (target?.closest?.('.textLayer')) return true

    return Array.from(mutation.addedNodes).some(node => {
      if (node.nodeType !== Node.ELEMENT_NODE) return false
      return node.matches?.('.textLayer') || node.querySelector?.('.textLayer')
    })
  }

  // --- Matching ---

  /**
   * Find the pdfViewer element — look inside viewerContainer target
   * or search the entire controller element for the .pdfViewer div.
   */
  findPdfViewer() {
    // The .pdfViewer div is created by pdf.js inside the viewerContainer
    const candidates = [
      this.hasViewerContainerTarget ? this.viewerContainerTarget : null,
      this.element
    ].filter(Boolean)

    for (const root of candidates) {
      const viewer = root.querySelector('.pdfViewer')
      if (viewer) return viewer
    }
    return null
  }

  runMatching() {
    if (this._matchingInProgress) return

    const viewerEl = this.findPdfViewer()
    if (!viewerEl) return

    // Store reference to the viewer container (parent of .pdfViewer) for overlay
    this._viewerEl = viewerEl

    // A scanned page has no text layer, so there is nothing to search and the whole
    // review overlay silently does nothing. Fill those pages from the stored word boxes;
    // matching below then proceeds identically for scans and born-digital PDFs.
    //
    // Non-blocking by design. Whatever has arrived is used now, and pages still in
    // flight fill in on a later pass. A document with a text layer never enters this at
    // all, because no page reports as needing boxes.
    const missing = pagesNeedingWordBoxes(viewerEl).filter(page => !this._wordBoxPages.has(page))
    if (missing.length) this.loadWordBoxesFor(missing)
    fillAvailableTextLayers(viewerEl, this._wordBoxPages)

    this.pageSpans = collectAllSpans(viewerEl)

    // Still nothing means the text layer has not rendered yet.
    let totalSpans = 0
    for (const [, spans] of this.pageSpans) totalSpans += spans.length
    if (totalSpans === 0) return

    const fields = this.fieldsValue
    const CHUNK_THRESHOLD = 80

    if (fields.length <= CHUNK_THRESHOLD) {
      // Small field set: match synchronously (fast enough)
      this.matchResults = matchAllFields(fields, this.pageSpans, viewerEl)
      this._finishMatching()
    } else {
      // Large field set: match in async chunks to keep UI responsive
      this._matchingInProgress = true
      matchFieldsChunked(fields, this.pageSpans, viewerEl, {
        chunkSize: 40,
        onProgress: (partial) => {
          // Update overlay incrementally as chunks complete
          this.matchResults = partial
          this._renderOverlayPartial()
        }
      }).then(results => {
        this._matchingInProgress = false
        this.matchResults = results
        this._finishMatching()
      })
    }
  }

  /**
   * A read the check doubts is usually not on the page at all, so nothing would
   * be outlined. When the check found the page's value, outline that instead
   * (status "found"), so the eye goes to what the page actually says.
   */
  outlineFoundValues() {
    if (!this.matchResults || !this.pageSpans) return

    this.fieldsValue.forEach(field => {
      const found = field.doubt?.found
      if (found == null || found === '' || this.matchResults.get(field.path)?.status === 'matched') return

      const result = findValueMatches(String(found), this.pageSpans, field.path)
      if (result.matches?.length) this.matchResults.set(field.path, { ...result, status: 'found' })
    })
  }

  _renderOverlayPartial() {
    if (!this.overlay) {
      this.overlay = new HighlightOverlay(this._viewerEl, {
        scrollContainer: this.hasViewerContainerTarget ? this.viewerContainerTarget : null,
        onHighlightClick: (fieldPath) => this.scrollToField(fieldPath),
        onHighlightHover: (fieldPath, match, isEnter) => this.highlightFieldRow(fieldPath, isEnter),
        onHighlightAction: (fieldPath, action) => this.handleHighlightAction(fieldPath, action),
        getFieldSummary: (fieldPath) => this.getFieldSummary(fieldPath)
      })
    }
    const reviewEntries = this.store?.getReviewEntries() || new Map()
    this.overlay.render(this.matchResults, reviewEntries)
    this.debouncedUpdateFieldStatuses()
    this.updateProgress()
    this.updateGuide()
  }

  _finishMatching() {
    this.outlineFoundValues()
    if (!this.overlay) {
      this.overlay = new HighlightOverlay(this._viewerEl, {
        scrollContainer: this.hasViewerContainerTarget ? this.viewerContainerTarget : null,
        onHighlightClick: (fieldPath) => this.scrollToField(fieldPath),
        onHighlightHover: (fieldPath, match, isEnter) => this.highlightFieldRow(fieldPath, isEnter),
        onHighlightAction: (fieldPath, action) => this.handleHighlightAction(fieldPath, action),
        getFieldSummary: (fieldPath) => this.getFieldSummary(fieldPath)
      })
    }

    const reviewEntries = this.store?.getReviewEntries() || new Map()
    this.overlay.render(this.matchResults, reviewEntries)

    if (!this._hasPlayedIntroPulse) {
      this.overlay.pulseAllHighlights()
      this._hasPlayedIntroPulse = true
    }

    this.updateFieldStatuses()
    this.updateProgress()
    this.updateGuide()

    // If many fields are unmatched, retry — text layer may still be settling
    const retryCount = this._retryCount || 0
    if (retryCount < 3) {
      let unmatchedCount = 0
      for (const [, r] of this.matchResults) {
        if (r.status === 'unmatched') unmatchedCount++
      }
      if (unmatchedCount > this.fieldsValue.length * 0.25) {
        this._retryCount = retryCount + 1
        setTimeout(() => this.runMatching(), 1000 * (retryCount + 1))
      }
    }
  }

  // --- Bidirectional navigation ---

  handleReviewClickCapture(event) {
    if (!this.activeValue) return
    if (event.target.closest('.pdf-review-slot, .pdf-review-popover, button, a, input, textarea, select, [contenteditable="true"]')) return

    const row = event.target.closest('[data-pdf-review-target~="fieldRow"]')
    if (!row || !this.element.contains(row)) return

    event.preventDefault()
    event.stopImmediatePropagation()

    const fieldPath = row.dataset.fieldPath
    if (!fieldPath) return
    this._showDone = false
    this.focusFieldMatch(fieldPath)
  }

  scrollToField(fieldPath) {
    const row = this._fieldRowByPath(fieldPath)
    if (!row) return

    this.scrollRowIntoDataPanel(row)
    row.classList.add('pdf-review-field--flash')
    setTimeout(() => row.classList.remove('pdf-review-field--flash'), 1500)
  }

  scrollRowIntoDataPanel(row) {
    const scroller = this.dataPanelScroller()
    if (!scroller) return

    const rowRect = row.getBoundingClientRect()
    const scrollerRect = scroller.getBoundingClientRect()
    const isVisible = rowRect.top >= scrollerRect.top && rowRect.bottom <= scrollerRect.bottom
    if (isVisible) return

    const targetTop = scroller.scrollTop +
      (rowRect.top - scrollerRect.top) -
      (scroller.clientHeight / 2) +
      (rowRect.height / 2)

    scroller.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth'
    })
  }

  dataPanelScroller() {
    if (!this.hasDataPanelTarget) return null

    return this.dataPanelTarget.querySelector('[data-extracted-data-editor-target~="formView"]') ||
      this.dataPanelTarget.querySelector('.pdf-review-data-scroll') ||
      this.dataPanelTarget
  }

  dataEditorController() {
    if (!this.hasDataPanelTarget) return null

    const editorElement = this.dataPanelTarget.querySelector('[data-controller~="extracted-data-editor"]')
    if (!editorElement) return null

    return this.application.getControllerForElementAndIdentifier(editorElement, 'extracted-data-editor')
  }

  enterDataEditorReviewMode() {
    const editor = this.dataEditorController()
    if (editor?.enterReviewMode) {
      editor.enterReviewMode()
      return
    }

    this.forceFormViewFallback()
  }

  exitDataEditorReviewMode() {
    this.dataEditorController()?.exitReviewMode?.()
  }

  forceFormViewFallback() {
    if (!this.hasDataPanelTarget) return

    const formView = this.dataPanelTarget.querySelector('[data-extracted-data-editor-target~="formView"]')
    const jsonView = this.dataPanelTarget.querySelector('[data-extracted-data-editor-target~="jsonView"]')
    formView?.classList.remove('hidden')
    jsonView?.classList.add('hidden')
  }

  handleFieldUpdated(event) {
    const { fieldPath, value } = event.detail || {}
    if (!fieldPath) return

    const field = this.fieldsValue.find(candidate => candidate.path === fieldPath)
    if (field) field.value = value
    this._fieldsByPath = null

    if (this.activeValue) {
      // A value fixed during review has been checked by a person: record it
      // and move on to the next field still to check.
      if (field) {
        this.markFieldReviewed(fieldPath, 'corrected')
        this.advanceToNextUnreviewed(this.getReviewableFieldPaths())
      }
      this.runMatching()
    }
    this.updateGuide()
  }

  handleReviewSyncError(error) {
    this._reviewSyncError = error?.message || 'Review changes could not be saved.'
    this.updateGuide()
  }

  clearReviewSyncError() {
    if (!this._reviewSyncError) return

    this._reviewSyncError = null
    this.updateGuide()
  }

  focusFieldMatch(fieldPath) {
    if (!fieldPath) return

    const reviewableRows = this.getReviewableFieldPaths()
    const focusedIndex = reviewableRows.indexOf(fieldPath)
    if (focusedIndex >= 0) this._focusedFieldIndex = focusedIndex

    this.selectFieldRow(fieldPath)
    if (this.overlay) {
      this.overlay.setCurrent(fieldPath)
      this.overlay.scrollToHighlight(fieldPath)
      this.overlay.previewHighlight(fieldPath)
      setTimeout(() => this.overlay?.clearPreview(fieldPath), 1600)
    }
    this.updateGuide()
  }

  selectFieldRow(fieldPath) {
    // Remove previous selection (O(1) via cached reference)
    if (this._selectedRow) {
      this._selectedRow.classList.remove('pdf-review-field--selected')
    }
    const row = this._fieldRowByPath(fieldPath)
    if (row) {
      row.classList.add('pdf-review-field--selected')
      this._selectedRow = row
    }
  }

  highlightFieldRow(fieldPath, isEnter) {
    const row = this._fieldRowByPath(fieldPath)
    if (!row) return

    if (isEnter) {
      row.classList.add('pdf-review-field--hover')
    } else {
      row.classList.remove('pdf-review-field--hover')
    }
  }

  previewField(event) {
    if (!this.activeValue || !this.overlay) return

    const fieldPath = event.currentTarget.dataset.fieldPath
    if (!fieldPath) return

    this.overlay.previewHighlight(fieldPath)
  }

  clearFieldPreview(event) {
    if (!this.overlay) return

    const fieldPath = event.currentTarget.dataset.fieldPath
    if (!fieldPath) return

    this.overlay.clearPreview(fieldPath)
  }

  // --- Review actions ---

  /**
   * Keyboard shortcuts for rapid review:
   *   ↑/↓ or j/k — navigate fields
   *   Enter or . — approve focused field
   *   f — flag focused field
 *   e — fix (edit) focused field
   *   Escape — exit review mode
   */
  handleKeydown(event) {
    if (!this.activeValue) return
    // Don't intercept when typing in inputs
    if (event.target.matches('input, textarea, select, [contenteditable="true"]')) return
    if (event.metaKey || event.ctrlKey || event.altKey) return
    // Enter on a focused button presses that button; do not also approve.
    if (event.key === 'Enter' && event.target.closest('button, a')) return

    const reviewableRows = this.getReviewableFieldPaths()
    if (reviewableRows.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
      case 'j':
        event.preventDefault()
        this.navigateField(1, reviewableRows)
        break
      case 'ArrowUp':
      case 'k':
        event.preventDefault()
        this.navigateField(-1, reviewableRows)
        break
      case 'Enter':
      case '.':
        event.preventDefault()
        if (this.guideIsDone()) {
          // A quick second Enter right after the last answer would skip the
          // summary unseen, so it waits a moment.
          if (Date.now() - (this._doneShownAt || 0) > 600) this.goToNextPaperwork()
          break
        }
        // A field the check doubts: Enter takes the page's value rather than
        // confirming the read it doubts.
        if (this.canEditFields() && this.foundValue(reviewableRows[this._focusedFieldIndex])) {
          this.editCurrentField(reviewableRows, { value: this.foundValue(reviewableRows[this._focusedFieldIndex]) })
          break
        }
        this.approveCurrentField(reviewableRows)
        break
      case 'f':
        event.preventDefault()
        this.flagCurrentField(reviewableRows)
        break
      case 'e':
        if (!this.canEditFields()) break
        event.preventDefault()
        this.editCurrentField(reviewableRows)
        break
      case 'Escape':
        event.preventDefault()
        this.activeValue = false
        this.clearReviewContinues()
        break
    }
  }

  navigateField(direction, reviewableRows) {
    if (reviewableRows.length === 0) return
    this._showDone = false

    this._focusedFieldIndex += direction
    if (this._focusedFieldIndex < 0) this._focusedFieldIndex = reviewableRows.length - 1
    if (this._focusedFieldIndex >= reviewableRows.length) this._focusedFieldIndex = 0

    const fieldPath = reviewableRows[this._focusedFieldIndex]
    this.focusFieldMatch(fieldPath)
    this.scrollToField(fieldPath)
    this.updateGuide()
  }

  approveCurrentField(reviewableRows) {
    if (this._focusedFieldIndex < 0 || this._focusedFieldIndex >= reviewableRows.length) return
    const fieldPath = reviewableRows[this._focusedFieldIndex]
    const status = this.matchResults?.get(fieldPath)?.status || 'matched'
    // Kept against a doubt: the read itself was not on the page.
    this.markFieldReviewed(fieldPath, status === 'found' ? 'unmatched' : status)
    // Auto-advance to next unreviewed
    this.advanceToNextUnreviewed(reviewableRows)
    this.updateGuide()
  }

  flagCurrentField(reviewableRows) {
    if (this._focusedFieldIndex < 0 || this._focusedFieldIndex >= reviewableRows.length) return
    const fieldPath = reviewableRows[this._focusedFieldIndex]
    this.markFieldReviewed(fieldPath, 'flagged')
    this.advanceToNextUnreviewed(reviewableRows)
    this.updateGuide()
  }

  advanceToNextUnreviewed(reviewableRows) {
    const startIdx = this._focusedFieldIndex + 1
    for (let i = 0; i < reviewableRows.length; i++) {
      const idx = (startIdx + i) % reviewableRows.length
      if (!this.store?.isReviewed(reviewableRows[idx])) {
        this._focusedFieldIndex = idx
        const fieldPath = reviewableRows[idx]
        this.focusFieldMatch(fieldPath)
        this.scrollToField(fieldPath)
        this.updateGuide()
        return
      }
    }
    this.updateGuide()
  }

  /**
   * Every reviewable field that has a row in the panel. Values too short to
   * locate on a PDF are still checked by the person, so they stay.
   */
  getReviewableFieldPaths() {
    return this.fieldsValue
      .map(field => field.path)
      .filter(path => path && this._fieldRowByPath(path))
  }

  reviewProgress(paths = this.getReviewableFieldPaths()) {
    let reviewed = 0
    let flagged = 0
    for (const path of paths) {
      if (!this.store?.isReviewed(path)) continue
      reviewed++
      if (this.store.isFlagged(path)) flagged++
    }
    return { reviewed, flagged, total: paths.length, remaining: paths.length - reviewed }
  }

  /** The mark on a row: marks the field correct, or undoes its review. */
  toggleFieldReviewed(event) {
    const fieldPath = event.currentTarget.dataset.fieldPath
    if (!fieldPath || !this.store) return

    if (this.store.isReviewed(fieldPath)) {
      this._showDone = false
      this.unmarkField(fieldPath)
    } else {
      this.markFieldReviewed(fieldPath, this.matchResults?.get(fieldPath)?.status || 'unmatched')
    }
    this.focusFieldMatch(fieldPath)
  }

  unmarkField(fieldPath) {
    if (!fieldPath || !this.store) return

    this.store.unmarkReviewed(fieldPath)
    const status = this.matchResults?.get(fieldPath)?.status || 'unmatched'
    this.overlay?.updateHighlightStatus(fieldPath, status)
    this.updateFieldStatuses()
    this.updateGuide()
  }

  handleHighlightAction(fieldPath, action) {
    if (!fieldPath) return

    if (action === 'flag') {
      this.markFieldReviewed(fieldPath, 'flagged')
    } else {
      const matchStatus = this.matchResults?.get(fieldPath)?.status || 'matched'
      this.markFieldReviewed(fieldPath, matchStatus)
    }

    this.scrollToField(fieldPath)
    this.updateGuide()
  }

  markFieldReviewed(fieldPath, matchStatus = 'matched') {
    if (!this.store) return

    this.store.markReviewed(fieldPath, { matchStatus })
    this._showDone = true
    this.overlay?.updateHighlightStatus(fieldPath, matchStatus === 'flagged' ? 'flagged' : 'reviewed')
    this.updateFieldStatuses()
    this.updateGuide()
  }

  checkSection(event) {
    const section = event.currentTarget.dataset.section
    if (!section || !this.store) return

    const sectionFields = this.fieldsValue.filter(f => f.section === section)
    const paths = sectionFields.map(f => f.path)
    this.store.markBulkReviewed(paths, { matchStatus: 'bulk' })

    paths.forEach(path => this.overlay?.updateHighlightStatus(path, 'reviewed'))

    this.updateFieldStatuses()
    this.updateProgress()
    this.updateGuide()
  }

  /** Marks every field not yet checked as correct. Flagged fields stay flagged. */
  markRestReviewed() {
    if (!this.store) return

    const paths = this.getReviewableFieldPaths().filter(path => !this.store.isReviewed(path))
    if (paths.length === 0) return

    this.store.markBulkReviewed(paths, { matchStatus: 'bulk' })
    this._showDone = true

    // Single overlay re-render instead of per-field updates
    if (this.overlay && this.matchResults) {
      this.overlay.render(this.matchResults, this.store.getReviewEntries())
    }

    this.updateFieldStatuses()
    this.updateGuide()
  }

  // --- Review guide ---
  //
  // One card at the top of the data panel walks the person through each
  // field: where it is, what it says, and three answers (Correct, Fix, Flag).
  // It lives beside the data rather than over the document, so it works the
  // same for a PDF, an email, or anything else with extracted data.

  handleGuideClick(event) {
    const button = event.target.closest('[data-review-action]')
    if (!button || !this.hasGuideTarget || !this.guideTarget.contains(button)) return

    event.preventDefault()
    const reviewableRows = this.getReviewableFieldPaths()
    const action = button.dataset.reviewAction

    if (action === 'finish') {
      this.activeValue = false
      this.clearReviewContinues()
      return
    }
    if (action === 'next-paperwork') {
      this.goToNextPaperwork()
      return
    }
    if (reviewableRows.length === 0) return

    switch (action) {
      case 'previous':
        this._showDone = false
        this.navigateField(-1, reviewableRows)
        break
      case 'next':
        this._showDone = false
        this.navigateField(1, reviewableRows)
        break
      case 'next-unreviewed':
        this.focusNextUnreviewed(reviewableRows)
        break
      case 'first-flagged':
        this._showDone = false
        this.focusFirstFlagged(reviewableRows)
        break
      case 'approve':
        this.ensureFocusedField(reviewableRows, { moveSelection: false })
        this.approveCurrentField(reviewableRows)
        break
      case 'flag':
        this.ensureFocusedField(reviewableRows, { moveSelection: false })
        this.flagCurrentField(reviewableRows)
        break
      case 'edit':
        this.editCurrentField(reviewableRows)
        break
      case 'undo':
        this.unmarkField(reviewableRows[this._focusedFieldIndex])
        break
      case 'summary':
        this._showDone = true
        this.updateGuide()
        break
      case 'rest':
        this.askToMarkRest()
        break
      case 'use-found':
        this.editCurrentField(reviewableRows, { value: this.foundValue(reviewableRows[this._focusedFieldIndex]) })
        break
      case 'confirm-exact':
        this.confirmExactMatches()
        break
      case 'rest-confirm':
        this._confirmingRest = false
        this.markRestReviewed()
        break
      case 'rest-cancel':
        this._confirmingRest = false
        this.updateGuide()
        break
    }
  }

  askToMarkRest() {
    this._confirmingRest = true
    clearTimeout(this._confirmRestTimer)
    this._confirmRestTimer = setTimeout(() => {
      this._confirmingRest = false
      this.updateGuide()
    }, 6000)
    this.updateGuide()
  }

  updateGuide() {
    if (!this.activeValue) return

    const reviewableRows = this.getReviewableFieldPaths()
    const progress = this.reviewProgress(reviewableRows)
    this.updateProgress(progress)
    if (!this.hasGuideTarget) return

    if (!this._guideClickBound) {
      this.guideTarget.addEventListener('click', event => this.handleGuideClick(event))
      this._guideClickBound = true
    }

    if (reviewableRows.length === 0) {
      this.guideTarget.innerHTML = `
        <div class="review-guide__card review-guide__card--empty">
          <div class="review-guide__title">Nothing to review</div>
          <div class="review-guide__hint">This paperwork has no extracted fields to check.</div>
          <div class="review-guide__actions">
            <button type="button" class="btn btn-sm review-guide__primary" data-review-action="finish">Close review</button>
          </div>
        </div>
      `
      return
    }

    // The summary shows once the last field is answered. Picking a field
    // afterwards shows that field, so an answer can still be changed.
    if (progress.remaining === 0 && this._showDone !== false) {
      this.overlay?.setCurrent(null)
      if (!this.guideIsDone()) this._doneShownAt = Date.now()
      this.guideTarget.innerHTML = this.renderGuideDone(progress)
      return
    }

    const fieldPath = this.ensureFocusedField(reviewableRows, { moveSelection: false })
    this.overlay?.setCurrent(fieldPath)
    this.guideTarget.innerHTML = this.renderGuideField(fieldPath, reviewableRows, progress)
  }

  renderGuideField(fieldPath, reviewableRows, progress) {
    const summary = this.getFieldSummary(fieldPath)
    const reviewed = this.store?.isReviewed(fieldPath)
    const flagged = this.store?.isFlagged(fieldPath)
    const corrected = this.store?.getEntry(fieldPath)?.match_status === 'corrected'
    const context = this.fieldContext(fieldPath)
    const canEdit = this.canEditFields()

    const stateTape = flagged
      ? '<span class="state-tape state-tape--sm" data-tone="bad"><i class="ti ti-flag"></i> Flagged</span>'
      : (reviewed
        ? `<span class="state-tape state-tape--sm" data-tone="done"><i class="ti ti-check"></i> ${corrected ? 'Fixed' : 'Correct'}</span>`
        : '')

    const fixButton = canEdit ? `
        <button type="button" class="btn btn-sm review-guide__secondary" data-review-action="edit" title="Type the right value (E)">
          <i class="ti ti-pencil"></i> Fix <kbd>E</kbd>
        </button>` : ''
    const onward = progress.remaining > 0
      ? `<button type="button" class="btn btn-sm review-guide__primary" data-review-action="next-unreviewed">
          Next to check <i class="ti ti-arrow-right"></i>
        </button>`
      : '<button type="button" class="btn btn-sm review-guide__primary" data-review-action="summary">Back to summary</button>'
    const doubt = this.fieldByPath(fieldPath)?.doubt
    const found = canEdit && !reviewed ? this.foundValue(fieldPath) : null
    const answers = found
      ? `
        <button type="button" class="btn btn-sm review-guide__primary" data-review-action="use-found" title="Type the value on the page (Enter)">
          Use ${escapeHtml(found)} <kbd>↵</kbd>
        </button>
        <button type="button" class="btn btn-sm review-guide__secondary" data-review-action="approve" title="The read is right after all">Keep</button>
        <button type="button" class="btn btn-sm review-guide__secondary" data-review-action="flag" title="Wrong, and needs someone to look (F)">
          <i class="ti ti-flag"></i> Flag <kbd>F</kbd>
        </button>
      `
      : reviewed
      ? `
        ${flagged ? fixButton : onward}
        ${flagged ? onward.replace('review-guide__primary', 'review-guide__secondary') : ''}
        <button type="button" class="btn btn-sm btn-ghost review-guide__quiet" data-review-action="undo">Undo</button>
      `
      : `
        <button type="button" class="btn btn-sm review-guide__primary" data-review-action="approve" title="The value matches the paperwork (Enter)">
          <i class="ti ti-check"></i> Correct <kbd>↵</kbd>
        </button>
        ${fixButton}
        <button type="button" class="btn btn-sm review-guide__secondary" data-review-action="flag" title="Wrong, and needs someone to look (F)">
          <i class="ti ti-flag"></i> Flag <kbd>F</kbd>
        </button>
      `

    const rest = progress.remaining - (reviewed ? 0 : 1)
    const exact = this.exactMatchPaths(reviewableRows).length
    const restLine = this._confirmingRest
      ? `
        <span class="review-guide__foot-text">Mark ${progress.remaining} unchecked ${pluralize(progress.remaining, 'field')} correct?</span>
        <span class="review-guide__foot-actions">
          <button type="button" class="review-guide__link review-guide__link--strong" data-review-action="rest-confirm">Yes, mark correct</button>
          <button type="button" class="review-guide__link" data-review-action="rest-cancel">Cancel</button>
        </span>
      `
      : exact > 0
      ? `
        <span class="review-guide__foot-text">${exact} ${pluralize(exact, 'value')} match the page exactly</span>
        <button type="button" class="review-guide__link review-guide__link--strong" data-review-action="confirm-exact" title="Mark each value outlined in exactly one place as correct">Confirm ${exact === 1 ? 'it' : 'them'}</button>
      `
      : `
        <span class="review-guide__foot-text">${progress.flagged ? `${progress.flagged} flagged` : ''}</span>
        ${rest > 0 ? `<button type="button" class="review-guide__link" data-review-action="rest" title="Mark every field you have not checked as correct">Mark the rest correct</button>` : ''}
      `

    const syncAlert = this._reviewSyncError
      ? `<div class="review-guide__alert" title="${escapeHtml(this._reviewSyncError)}"><i class="ti ti-alert-triangle"></i> Your last answer was not saved. It will try again.</div>`
      : ''

    return `
      <div class="review-guide__card${flagged ? ' review-guide__card--flagged' : ''}${reviewed && !flagged ? ' review-guide__card--done' : ''}">
        <div class="review-guide__top">
          <span class="review-guide__step">${context ? escapeHtml(context) : ''}</span>
          <span class="review-guide__nav">
            <button type="button" class="btn btn-xs btn-circle btn-ghost" data-review-action="previous" title="Previous field (↑ or K)" aria-label="Previous field"><i class="ti ti-chevron-up"></i></button>
            <button type="button" class="btn btn-xs btn-circle btn-ghost" data-review-action="next" title="Next field (↓ or J)" aria-label="Next field"><i class="ti ti-chevron-down"></i></button>
          </span>
        </div>
        <div class="review-guide__title-row">
          <span class="review-guide__title" title="${escapeHtml(summary.fullLabel || summary.label)}">${escapeHtml(summary.label)}</span>
          ${stateTape}
        </div>
        <div class="review-guide__value${summary.empty ? ' review-guide__value--empty' : ''}${summary.mono ? ' xd-v--mono' : ''}">${escapeHtml(summary.value)}</div>
        ${doubt?.reason && !reviewed ? `<div class="review-guide__doubt"><i class="ti ti-alert-circle"></i> ${escapeHtml(doubt.reason)}</div>` : this.fieldSourceHint(fieldPath)}
        <div class="review-guide__actions">${answers}</div>
        ${syncAlert}
      </div>
      <div class="review-guide__foot">${restLine}</div>
    `
  }

  renderGuideDone(progress) {
    const flaggedLine = progress.flagged
      ? `${progress.flagged} ${pluralize(progress.flagged, 'field')} flagged for someone to look at.`
      : 'Nothing is flagged.'
    const next = this.nextPaperworkButton()
    const position = this.paperworkPosition()

    const primary = next
      ? `<button type="button" class="btn btn-sm review-guide__primary" data-review-action="next-paperwork" title="Open the next paperwork and keep reviewing (Enter)">Next paperwork</button>
         <button type="button" class="btn btn-sm review-guide__secondary" data-review-action="finish">Finish review</button>`
      : '<button type="button" class="btn btn-sm review-guide__primary" data-review-action="finish">Finish review</button>'
    const flagged = progress.flagged
      ? '<button type="button" class="btn btn-sm review-guide__secondary" data-review-action="first-flagged">Go to flagged</button>'
      : ''

    return `
      <div class="review-guide__card review-guide__card--done">
        <div class="review-guide__title-row">
          <span class="review-guide__title">All ${progress.total} ${pluralize(progress.total, 'field')} checked</span>
          <span class="state-tape state-tape--sm" data-tone="${progress.flagged ? 'bad' : 'done'}">${progress.flagged ? 'Flagged' : 'Reviewed'}</span>
        </div>
        <div class="review-guide__hint">${flaggedLine} Click any field to change your answer.${position ? ` This is paperwork ${position}.` : ''}</div>
        <div class="review-guide__actions">${primary}${flagged}</div>
      </div>
    `
  }

  guideIsDone() {
    return this.hasGuideTarget && Boolean(this.guideTarget.querySelector('.review-guide__card--done'))
  }

  // ---- Moving through a workflow's paperwork in review -----------------------

  // The viewer's pager (paperwork_attachments/_viewer_container) owns the order.
  nextPaperworkButton() {
    const button = this.element.closest('.viewer-shell')?.querySelector('.viewer-pager [data-action*="attachments-modal#next"]')
    return button && !button.disabled ? button : null
  }

  paperworkPosition() {
    const count = this.element.closest('.viewer-shell')?.querySelector('.viewer-pager__count')
    return count ? count.textContent.replace(/\s+/g, ' ').trim() : ''
  }

  goToNextPaperwork() {
    const button = this.nextPaperworkButton()
    if (!button) {
      this.activeValue = false
      return
    }
    // disconnect leaves the note, and the next paperwork starts in review.
    button.click()
  }

  noteReviewContinues() {
    try {
      window.sessionStorage.setItem(this.constructor.CONTINUE_KEY, String(Date.now() + this.constructor.CONTINUE_MS))
    } catch (_error) {
      // Blocked storage: review just does not carry on to the next paperwork.
    }
  }

  clearReviewContinues() {
    try {
      window.sessionStorage.removeItem(this.constructor.CONTINUE_KEY)
    } catch (_error) {
      // Nothing to clear.
    }
  }

  consumeReviewContinues() {
    try {
      const until = Number(window.sessionStorage.getItem(this.constructor.CONTINUE_KEY))
      window.sessionStorage.removeItem(this.constructor.CONTINUE_KEY)
      return until > Date.now()
    } catch (_error) {
      return false
    }
  }

  // ---- Where the review stands, before it is opened ---------------------------
  // The Review button says it: "Review", "Resume · 12 left", "Reviewed" or
  // "3 flagged". The state and the field list load when the viewer is idle (the
  // same two requests opening review makes), so opening is instant.

  buildReviewStore() {
    return new ReviewStore(this.paperworkIdValue, {
      reviewStateUrl: this.reviewStateUrlValue,
      onSyncError: error => this.handleReviewSyncError(error),
      onSyncSuccess: () => this.clearReviewSyncError()
    })
  }

  scheduleStatusPrefetch() {
    // A share-link or task-token view sends no review requests. They are for signed-in users.
    if (this.isPublicValue) return
    if (!this.hasToggleBtnTarget || !this.hasReviewStateUrlValue || !this.hasReviewableFieldsUrlValue) return

    const run = () => this.prefetchReviewStatus()
    if (window.requestIdleCallback) {
      this._prefetchIdle = window.requestIdleCallback(run, { timeout: 1500 })
    } else {
      this._prefetchTimer = setTimeout(run, 400)
    }
  }

  async prefetchReviewStatus() {
    if (this._prefetching || this.activeValue) return

    this._prefetching = true
    try {
      this.store ||= this.buildReviewStore()
      await Promise.all([this.store.hydrate(), this.loadReviewableFields()])
      this._statusReady = true
    } catch (_error) {
      // The button stays "Review"; opening review reports any real problem.
    } finally {
      this._prefetching = false
    }

    this.updateToggleButton()
    if (this._statusReady && this.consumeReviewContinues() && this.statusSummary()?.total > 0) {
      this.activeValue = true
    }
  }

  // From the field list the server sent, not the rows on screen: large arrays
  // render lazily, so rows can be missing until review opens.
  statusSummary() {
    if (!this._statusReady || !this.store) return null

    const paths = this.fieldsValue.map(field => field.path).filter(Boolean)
    let reviewed = 0
    let flagged = 0
    paths.forEach(path => {
      if (!this.store.isReviewed(path)) return
      reviewed += 1
      if (this.store.isFlagged(path)) flagged += 1
    })
    return { total: paths.length, reviewed, flagged, remaining: paths.length - reviewed }
  }

  /** Where the person can see this value, in plain words. */
  // Said only when the outline cannot do the job: the value is in more than
  // one place, or nowhere on the page. A matched value is simply outlined.
  fieldSourceHint(fieldPath) {
    if (!this._viewerEl) return ''

    const line = (icon, text) => `<div class="review-guide__source"><i class="ti ${icon}"></i> ${text}</div>`
    switch (this.matchResults?.get(fieldPath)?.status) {
      case 'matched':
      case 'skipped':
      case 'found':
        return ''
      case 'multiple':
        return line('ti-focus-2', 'In more than one place on the page.')
      default:
        return line('ti-eye-off', 'Not found on the page.')
    }
  }

  /** The group a field sits in, such as "Line item 2". Empty for a top-level field. */
  fieldContext(fieldPath) {
    const field = this.fieldByPath(fieldPath)
    if (!field) return ''

    const full = this.humanFieldPath(field)
    const separator = full.lastIndexOf(' · ')
    return separator > 0 ? full.slice(0, separator) : ''
  }

  canEditFields() {
    return Boolean(this.dataEditorController()?.openFieldEditorForReview && this.dataEditorController()?.hasUrlValue)
  }

  focusFirstFlagged(reviewableRows = this.getReviewableFieldPaths()) {
    const index = reviewableRows.findIndex(path => this.store?.isFlagged(path))
    if (index < 0) return

    this._focusedFieldIndex = index
    this.focusFieldMatch(reviewableRows[index])
    this.scrollToField(reviewableRows[index])
  }

  ensureFocusedField(reviewableRows = this.getReviewableFieldPaths(), { moveSelection = true } = {}) {
    if (reviewableRows.length === 0) return null

    if (this._focusedFieldIndex < 0 || this._focusedFieldIndex >= reviewableRows.length) {
      const firstUnreviewed = reviewableRows.findIndex(path => !this.store?.isReviewed(path))
      this._focusedFieldIndex = firstUnreviewed >= 0 ? firstUnreviewed : 0
    }

    const fieldPath = reviewableRows[this._focusedFieldIndex]
    if (moveSelection) {
      this.focusFieldMatch(fieldPath)
      this.scrollToField(fieldPath)
    } else {
      this.selectFieldRow(fieldPath)
    }
    return fieldPath
  }

  focusNextUnreviewed(reviewableRows = this.getReviewableFieldPaths()) {
    if (reviewableRows.length === 0) return

    const startIdx = this._focusedFieldIndex < 0 ? 0 : this._focusedFieldIndex + 1
    for (let i = 0; i < reviewableRows.length; i++) {
      const idx = (startIdx + i) % reviewableRows.length
      if (!this.store?.isReviewed(reviewableRows[idx])) {
        this._focusedFieldIndex = idx
        const fieldPath = reviewableRows[idx]
        this.focusFieldMatch(fieldPath)
        this.scrollToField(fieldPath)
        this.updateGuide()
        return
      }
    }

    this.navigateField(1, reviewableRows)
  }

  editCurrentField(reviewableRows = this.getReviewableFieldPaths(), { value } = {}) {
    const fieldPath = this.ensureFocusedField(reviewableRows)
    if (!fieldPath) return

    this.scrollToField(fieldPath)
    this.dataEditorController()?.openFieldEditorForReview?.(fieldPath, { value })
    this.updateGuide()
  }

  /** The value a check found on the page for this field, when it doubts the read. */
  foundValue(fieldPath) {
    const found = this.fieldByPath(fieldPath)?.doubt?.found
    return found == null || found === '' ? null : String(found)
  }

  /**
   * Fields a person need not look at one by one: outlined in exactly one place
   * by an exact text match (confidence 1.0), not doubted by any check, and not
   * yet answered. Number, date, fuzzy and repeated matches never count, nor a
   * short number in a line item ("2", "15"), which lands on the page by chance.
   */
  exactMatchPaths(reviewableRows = this.getReviewableFieldPaths()) {
    return reviewableRows.filter(path => {
      const field = this.fieldByPath(path)
      if (this.store?.isReviewed(path) || field?.doubt) return false
      if (/\.\d+(\.|$)/.test(path) && this.isShortNumber(field?.value)) return false
      const result = this.matchResults?.get(path)
      return result?.status === 'matched' && result.matches?.length === 1 && (result.confidence ?? 0) >= 1
    })
  }

  /** A number with fewer than three significant characters ("2", "1.00", "0.5"). */
  isShortNumber(value) {
    if (typeof value === 'number') return Number.isFinite(value) && this.significantDigits(String(value)) < 3
    const text = String(value ?? '').trim().replace(/[$€£,\s]/g, '')
    if (!/^[-+]?\d*\.?\d+%?$/.test(text)) return false
    return this.significantDigits(String(Number(text.replace('%', '')))) < 3
  }

  significantDigits(text) {
    return text.replace(/[^0-9]/g, '').replace(/^0+/, '').length
  }

  confirmExactMatches() {
    const paths = this.exactMatchPaths()
    if (!this.store || paths.length === 0) return

    this.store.markBulkReviewed(paths, { matchStatus: 'matched' })
    if (this.overlay && this.matchResults) this.overlay.render(this.matchResults, this.store.getReviewEntries())
    this.updateFieldStatuses()
    this._focusedFieldIndex = -1
    this.ensureFocusedField()
    this.updateGuide()
  }

  // --- UI updates ---

  updateToggleButton() {
    if (!this.hasToggleBtnTarget) return

    const button = this.toggleBtnTarget
    if (this.activeValue) {
      button.classList.add('btn-primary-action')
      button.textContent = 'Done'
      button.title = 'Stop reviewing (Esc)'
      delete button.dataset.reviewState
      return
    }

    button.classList.remove('btn-primary-action')
    const status = this.statusSummary()
    let label = 'Review'
    let state = 'todo'
    let title = 'Check each field against the paperwork'

    if (status && status.total > 0) {
      if (status.remaining === 0 && status.flagged > 0) {
        label = `${status.flagged} flagged`
        state = 'flagged'
        title = `All ${status.total} fields checked, ${status.flagged} flagged. Open the review.`
      } else if (status.remaining === 0) {
        label = 'Reviewed'
        state = 'done'
        title = `All ${status.total} fields checked. Open the review.`
      } else if (status.reviewed > 0) {
        label = `Resume · ${status.remaining} left`
        state = 'partial'
        title = `${status.reviewed} of ${status.total} fields checked${status.flagged ? `, ${status.flagged} flagged` : ''}. Pick up where you stopped.`
      }
    }

    button.textContent = label
    button.title = title
    button.dataset.reviewState = state
  }

  updateProgress(progress = this.reviewProgress()) {
    if (!this.hasProgressChipTarget) return
    if (!this.activeValue) {
      this.progressChipTarget.classList.add('hidden')
      return
    }

    const percent = progress.total > 0 ? Math.round((progress.reviewed / progress.total) * 100) : 0
    this.progressChipTarget.classList.remove('hidden')
    this.progressChipTarget.title = `${progress.reviewed} of ${progress.total} checked${progress.flagged > 0 ? `, ${progress.flagged} flagged` : ''}`
    this.progressChipTarget.setAttribute('aria-label', this.progressChipTarget.title)
    this.progressChipTarget.innerHTML = `
      <span class="pdf-review-progress-count"><strong>${progress.reviewed}</strong><span>of ${progress.total} checked</span></span>
      <span class="pdf-review-progress-track" aria-hidden="true">
        <span class="pdf-review-progress-fill" style="width: ${percent}%"></span>
      </span>
    `
  }

  /** Cached Map<fieldPath, rowElement> — invalidated on connect/disconnect */
  _fieldRowByPath(fieldPath) {
    if (!this._fieldRowMap) {
      this._fieldRowMap = new Map()
      this.fieldRowTargets.forEach(row => {
        if (row.dataset.fieldPath) this._fieldRowMap.set(row.dataset.fieldPath, row)
      })
    }
    return this._fieldRowMap.get(fieldPath) || null
  }

  /** Invalidate cache when targets change */
  fieldRowTargetConnected() { this._fieldRowMap = null }
  fieldRowTargetDisconnected() { this._fieldRowMap = null }

  fieldByPath(fieldPath) {
    if (!this._fieldsByPath) {
      this._fieldsByPath = new Map()
      this.fieldsValue.forEach(f => this._fieldsByPath.set(f.path, f))
    }
    return this._fieldsByPath.get(fieldPath) || null
  }

  getFieldSummary(fieldPath) {
    const field = this.fieldByPath(fieldPath)
    if (!field) return { label: fieldPath, value: '' }

    const empty = field.value == null || field.value === ''
    const shown = empty ? null : displayFor(leafKey(field.path), field.value)
    return {
      label: this.shortFieldLabel(field),
      fullLabel: this.humanFieldPath(field),
      value: empty ? 'Not set' : shown.text,
      mono: Boolean(shown?.mono),
      empty
    }
  }

  /**
   * Short label — the leaf field name as the form row shows it.
   * "line_items.0.item_description" → "Item description"
   * "po_number" → "PO number"
   */
  shortFieldLabel(field) {
    const path = field.path || ''
    const shown = this._fieldRowByPath(path)?.querySelector('[data-role="label-text"]')?.textContent.trim()
    if (shown) return shown
    const parts = path.split('.')
    // Get the last non-numeric segment
    const leaf = parts.filter(p => !/^\d+$/.test(p)).pop() || path
    return titleize(leaf)
  }

  humanFieldPath(field) {
    const path = field.path || ''
    const parts = path.split('.')
    const arrayIndex = parts.findIndex((part, index) => index > 0 && /^\d+$/.test(part))

    if (arrayIndex > 0) {
      const section = singularizeTitle(parts[arrayIndex - 1])
      const itemNumber = parseInt(parts[arrayIndex], 10) + 1
      const leaf = titleize(parts.slice(arrayIndex + 1).join(' '))
      return `${section} ${itemNumber} · ${leaf}`
    }

    return field.label || titleize(path)
  }

  expandReviewSections(event) {
    event?.preventDefault()

    const root = this.hasDataPanelTarget ? this.dataPanelTarget : this.element
    root.querySelectorAll('details.extracted-data-details').forEach(details => {
      details.open = true
    })
  }

  collapseReviewSections(event) {
    event?.preventDefault()

    const root = this.hasDataPanelTarget ? this.dataPanelTarget : this.element
    root.querySelectorAll('details.extracted-data-details').forEach(details => {
      details.open = false
    })
  }

  /**
   * Debounced version of updateFieldStatuses — batches rapid calls
   * (e.g., during bulk operations) into a single RAF-synced update.
   */
  debouncedUpdateFieldStatuses() {
    if (this._statusDebounce) return
    this._statusDebounce = requestAnimationFrame(() => {
      this._statusDebounce = null
      this.updateFieldStatuses()
    })
  }

  updateFieldStatuses() {
    if (!this.matchResults) return

    const rows = this.fieldRowTargets
    const batchSize = 100

    // For small sets, update synchronously
    if (rows.length <= batchSize) {
      this._applyStatusBatch(rows, 0, rows.length)
      return
    }

    // For large sets, chunk across frames to avoid jank
    let offset = 0
    const applyChunk = () => {
      const end = Math.min(offset + batchSize, rows.length)
      this._applyStatusBatch(rows, offset, end)
      offset = end
      if (offset < rows.length) {
        requestAnimationFrame(applyChunk)
      }
    }
    requestAnimationFrame(applyChunk)
  }

  _applyStatusBatch(rows, start, end) {
    for (let i = start; i < end; i++) {
      const row = rows[i]
      const fieldPath = row.dataset.fieldPath
      if (!fieldPath) continue

      const mark = row.querySelector('.review-mark')
      const reviewed = this.store?.isReviewed(fieldPath)
      const flagged = this.store?.isFlagged(fieldPath)
      const state = flagged ? 'flagged' : (reviewed ? 'reviewed' : 'todo')

      row.classList.toggle('pdf-review-row--reviewed', state === 'reviewed')
      row.classList.toggle('pdf-review-row--flagged', state === 'flagged')
      if (!mark) continue

      mark.dataset.state = state
      mark.setAttribute('aria-pressed', reviewed ? 'true' : 'false')
      mark.querySelector('i').className = flagged ? 'ti ti-flag' : 'ti ti-check'
      const title = flagged ? 'Flagged. Click to undo' : (reviewed ? 'Checked. Click to undo' : 'Mark correct')
      mark.title = title
      mark.querySelector('.sr-only').textContent = title
    }
  }

  showFieldReviewUI() {
    const reviewableSet = new Set(this.fieldsValue.map(f => f.path))
    this.fieldRowTargets.forEach(row => {
      const reviewable = reviewableSet.has(row.dataset.fieldPath)
      row.classList.toggle('pdf-review-row--reviewable', reviewable)
      const reviewSlot = row.querySelector('.pdf-review-slot')
      if (reviewSlot && reviewable) reviewSlot.classList.remove('hidden')
    })
    this.sectionHeaderTargets.forEach(header => {
      const reviewSlot = header.querySelector('.pdf-review-section-slot')
      if (reviewSlot) reviewSlot.classList.remove('hidden')
    })
  }

  hideFieldReviewUI() {
    this.fieldRowTargets.forEach(row => {
      const reviewSlot = row.querySelector('.pdf-review-slot')
      if (reviewSlot) reviewSlot.classList.add('hidden')
      row.classList.remove('pdf-review-row--reviewable', 'pdf-review-row--reviewed', 'pdf-review-row--flagged', 'pdf-review-field--selected')
    })
    this.sectionHeaderTargets.forEach(header => {
      const reviewSlot = header.querySelector('.pdf-review-section-slot')
      if (reviewSlot) reviewSlot.classList.add('hidden')
    })
  }
}

function titleize(value) {
  return fieldLabel(value)
}

function singularizeTitle(value) {
  return titleize(value)
    .replace(/ies$/, 'y')
    .replace(/s$/, '')
}

function pluralize(count, word) {
  return count === 1 ? word : `${word}s`
}

function escapeHtml(value) {
  const div = document.createElement('div')
  div.textContent = value == null ? '' : String(value)
  return div.innerHTML
}
